import axios, { AxiosError } from 'axios'
import { useState, useEffect } from 'react'
import { Alias, ProductRemovalStatus, ProductNoticeStatus } from '../redux/types/products'
import { API_PRODUCTS } from '../consts'

export interface ProductIds {
  id: number
  uuid: string
}

export interface ProductMergeStatus {
  duplicate_product: ProductIds
  active_product: ProductIds
  initiated_time: string
  merged_time: string
  failed_at: string
}

export interface Product {
  amount: number
  brand: string
  brand_subtype: string
  category: string
  dosage: number
  id: number
  is_favorite: boolean
  kind_subtype: string | null
  kind: string
  lineage: string
  name: string
  root_subtype: string
  type: string
  url_slug: string
  net_weight_grams: number
  thc_dosage_milligrams: number
  cbd_dosage_milligrams: number
  quantity_value: number
  quantity_units: string
  description: string
  aliases: Alias[]
  image_urls: string[]
  matched_name: string
  matched_name_is_alias: boolean
  removal_status: ProductRemovalStatus
  notice_status: ProductNoticeStatus
  merge_status: ProductMergeStatus | null
  store_count: number | null
  store_id?: number
  states?: string[]
  uuid: string
}

export interface ProductResponse {
  product: Product
}

type UseProductState = {
  product: Product | null
  error: AxiosError<any> | null
  isLoading: boolean
}

export const useProduct = (
  id: string | number | null
): {
  refetchProduct: () => void
} & UseProductState => {
  const [state, setState] = useState<UseProductState>({
    product: null,
    error: null,
    isLoading: false,
  })
  const fetchProduct = async () => {
    if (!id) {
      setState({ error: null, product: null, isLoading: false })
      return
    }

    try {
      setState({ ...state, isLoading: true })
      const response = await axios.get<ProductResponse>(`${API_PRODUCTS}/${id}`)

      setState({ product: response.data.product, error: null, isLoading: false })
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setState({ ...state, error: err, product: null, isLoading: false })
        return
      }
      throw err
    }
  }

  useEffect(() => {
    fetchProduct()
  }, [id]) // eslint-disable-line

  return { ...state, refetchProduct: fetchProduct }
}
